import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { useUploadFile } from '../../hooks/useUploadFile';

import { selector_auth } from '../../reducer/auth';
import { BREAKPOINTS } from '../../../constants';
import { TRANSLATIONS } from '../../../translations';

import { Modal } from './Modal';
import { ModalClose } from './ModalClose';
import Drawer from '../../ui-components/Drawer/Drawer';
import { Input } from '../Input';
import { BetaTextInput } from '../BetaTextInput';
import Typography, {
  TypographyStates,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import DropZone from '../../ui-components/DropZone/DropZone';
import Button, {
  ButtonSizes,
  ButtonWidths,
  ButtonStyles,
} from '../Button/Button';

import { storeReview } from '../../hooks/interactsWithReviews';
import { StarInput } from './StarInput';
import { RadioList } from './RadioList';
import useBeforeUnload from '../../hooks/useBeforeUnload';
import { logBrazeWrittenReview } from '../../command/braze';
import endpoints from '../../define/endpoints';

const TITLE = 'title';
const DESCRIPTION = 'description';
const SCORE = 'score';
const SHARED_WITH = 'shared_with';
const MAX_FILE_SIZE = 100;
const MAX_FILES = 10;

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const ModalOrderListReview = ({
  order,
  isOpen,
  setIsOpen,
  shouldScroll,
  onFinish,
  actionToken,
  reviewId,
  draftData,
  setDraftData,
  saveDraftData,
}) => {
  const baseUrl = window.freedomeConfig.endpoints.base_url;
  const reviewTermsUrl = window.freedomeConfig.knownPages.reviewTerms;

  const formRef = useRef(null);
  const EDITABLE = 'editable';
  const isDeviceSmallerThanTablet = !useMatchesMinWidth(BREAKPOINTS.TABLET);
  const auth = useSelector(selector_auth);
  const { uploadFile, deleteFile } = useUploadFile(
    reviewId,
    'review.media',
    actionToken
  );

  const [isLoading, setIsLoading] = useState(false);
  const [hasFiles, setHasFiles] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Create a ref to store the latest values
  const latestValuesRef = useRef();
  latestValuesRef.current = { order, saveDraftData };

  // Debounced save function with 1500ms delay to avoid frequent saves
  const debouncedSave = useRef(
    debounce(() => {
      if (latestValuesRef.current.order?.reviewStatus !== EDITABLE) {
        latestValuesRef.current.saveDraftData();
      }
    }, 1500)
  ).current;

  // Create a debounced setDraftData for UI updates with shorter delay, needed to avoid performance issues
  // when the user types in the review form.
  const debouncedSetDraftData = useRef(
    debounce((newData) => {
      setDraftData(newData);
    }, 300)
  ).current;

  // trigger save action when the tab is closed or refreshed
  useBeforeUnload((event) => {
    if (order?.reviewStatus !== EDITABLE) {
      saveDraftData();
    }
  }, isOpen);

  const { register, handleSubmit, errors } = useForm({
    reValidateMode: 'onSubmit',
  });
  const handleUploadStatusChange = (value) => {
    setIsSubmitDisabled(value);
  };

  const sharedList = {
    'no-one': TRANSLATIONS.NO_ONE,
    couple: TRANSLATIONS.COUPLE,
    friends: TRANSLATIONS.FRIENDS,
    family: TRANSLATIONS.FAMILY,
    'co-workers': TRANSLATIONS.CO_WORKERS,
  };

  if (!order) {
    return null;
  }

  const titleMap = {
    reviewable: TRANSLATIONS.INSERT_REVIEW,
    editable: TRANSLATIONS.EDIT_REVIEW,
    draft: TRANSLATIONS.COMPLETE_REVIEW,
  };
  const findTitle = (reviewStatus) =>
    titleMap[reviewStatus] || TRANSLATIONS.INSERT_REVIEW;

  // This removes surplus media that was previously loaded before the DropZone refactor.
  let validMedia = draftData.media;
  if (
    draftData &&
    Array.isArray(draftData.media) &&
    draftData.media.length > MAX_FILES
  ) {
    validMedia = draftData.media.slice(
      draftData.media.length - MAX_FILES,
      draftData.media.length
    );

    const draftDataToRemove = draftData.media.slice(
      0,
      draftData.media.length - MAX_FILES
    );
    draftDataToRemove.forEach((file) => {
      deleteFile(file.id);
    });
  }

  const renderFormLabel = (text, isOptional = false) => (
    <div className="d-flex gap-base mt-3xs">
      <Typography
        classNames="modal-ord-li-review__review-form__label"
        variant="p3"
        weight={TypographyWeights['semi-bold']}
      >
        {text}
      </Typography>
      {isOptional && (
        <Typography variant="p3">({TRANSLATIONS.OPTIONAL})</Typography>
      )}
    </div>
  );

  const handleFileUpload = (file) => {
    setHasFiles(true);
    return uploadFile(file);
  };

  const handleFileDeletion = (file) => {
    return deleteFile(file.id);
  };

  async function onSubmit(data) {
    setIsLoading(true);
    const reviewData = {
      id: reviewId,
      id_order: order.id,
      [TITLE]: data[TITLE],
      [DESCRIPTION]: data[DESCRIPTION],
      [SCORE]: data[SCORE],
      [SHARED_WITH]: data[SHARED_WITH],
      status: 'published',
    };
    const response = await storeReview(reviewData, auth?.token, actionToken);
    setIsLoading(false);
    dataLayer.push({
      event: 'review',
      reviewRating: data[SCORE],
      productSKU: order.listing.id,
    });

    // Braze: log written_review event with score and activity title
    if (response) {
      const coverImage = order.listing?.coverImage;
      const coverImageUrl = coverImage ? `${endpoints.bucket}${coverImage}` : null;
      logBrazeWrittenReview(
        data[SCORE],
        order.listing?.title || null,
        data[TITLE],
        data[DESCRIPTION],
        coverImageUrl,
        order.listing?.slug || null
      );
    }

    onFinish(!!response);
  }

  const submitButton = (
    <Button
      style={ButtonStyles.secondary}
      size={ButtonSizes.s}
      width={
        isDeviceSmallerThanTablet ? ButtonWidths.isFull : ButtonWidths.auto
      }
      label={
        isSubmitDisabled ? TRANSLATIONS.UPLOADING : TRANSLATIONS.CONTACT_SEND
      }
      disabled={isSubmitDisabled}
      onClick={() => {
        formRef.current.dispatchEvent(
          new Event('submit', { cancelable: true })
        );
      }}
    />
  );

  const reviewForm = (
    <form
      ref={formRef}
      className="modal-ord-li-review__review-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div>
        {renderFormLabel(TRANSLATIONS.GENERAL_RATING)}
        <StarInput
          defaultValue={(draftData && draftData.score) || 0}
          inputName={SCORE}
          register={register}
          onChange={(scoreValue) => {
            const newData = {
              ...draftData,
              score: scoreValue,
            };
            debouncedSetDraftData(newData);
            debouncedSave();
          }}
        />
        {errors && errors.score && (
          <div className="drop-zone__error">
            <Typography
              variant="p4"
              weight={TypographyWeights.light}
              state={TypographyStates.error}
            >
              {TRANSLATIONS.REQUIRED_FIELD}
            </Typography>
          </div>
        )}
      </div>

      <BetaTextInput
        asTextArea
        className="modal-ord-li-review__review-form__field"
        errored={!!errors[DESCRIPTION]}
        label={TRANSLATIONS.ADD_REVIEW}
        name={DESCRIPTION}
        setRef={register({ required: false, max: 1000 })}
        dataGtmItem="model-order-review-form"
        dataGtmDetail="description"
        defaultValue={(draftData && draftData[DESCRIPTION]) || null}
        onChange={(event) => {
          const newData = {
            ...draftData,
            description: event.target.value,
          };
          debouncedSetDraftData(newData);
          debouncedSave();
        }}
        resize={true}
        limit={900}
      />

      <div className="mt-3xs">
        <BetaTextInput
          className="modal-ord-li-review__review-form__field"
          errored={!!errors[TITLE]}
          label={TRANSLATIONS.TITLE_REVIEW}
          name={TITLE}
          setRef={register({ required: false, max: 100 })}
          dataGtmItem="model-order-review-form"
          dataGtmDetail="title"
          defaultValue={(draftData && draftData[TITLE]) || null}
          onChange={(event) => {
            const newData = {
              ...draftData,
              title: event.target.value,
            };
            debouncedSetDraftData(newData);
            debouncedSave();
          }}
          resize={false}
          limit={100}
        />
      </div>

      {renderFormLabel(TRANSLATIONS.UPLOAD_MEDIA, true)}
      <DropZone
        label={TRANSLATIONS.MEDIA_MAX}
        handleFileUpload={handleFileUpload}
        handleFileDeletion={handleFileDeletion}
        maxFileSize={MAX_FILE_SIZE}
        maxFiles={MAX_FILES}
        hasFiles={hasFiles}
        setHasFiles={setHasFiles}
        draftMedia={validMedia}
        uploadStatusChange={handleUploadStatusChange}
      />

      {hasFiles && (
        <div
          className={cn('fake-checkbox', {
            'fake-checkbox--errored': !!errors.privacy,
          })}
          style={{ marginTop: 5 }}
        >
          <input
            className="fake-checkbox__input"
            type="checkbox"
            name="privacy"
            ref={register({ required: true })}
            id="partner-signup-privacy"
          />
          <label
            htmlFor="partner-signup-privacy"
            className="fake-checkbox__label"
          >
            <span className="iconify inline" data-icon="mdi:check-bold"></span>
          </label>
          <p className="fake-checkbox__text">
            {app_main_wpml.privacy_acceptance}{' '}
            <a
              href={`${baseUrl}${reviewTermsUrl}`}
              target="_blank"
            >
              {app_main_wpml.privacy_policy_and_terms_of_use}
            </a>{' '}
            {app_main_wpml.of_the_service}
          </p>
        </div>
      )}

      {renderFormLabel(TRANSLATIONS.WITH_WHOM, true)}

      <RadioList
        inputName={SHARED_WITH}
        options={sharedList}
        register={register}
        defaultValue={draftData && draftData.sharedWith}
        onChange={(value) => {
          const newData = {
            ...draftData,
            sharedWith: value,
          };
          debouncedSetDraftData(newData);
          debouncedSave();
        }}
      />
    </form>
  );

  const experienceDescription = (
    <>
      {order.type != 'giftcard' ? (
        <p className="modal-ord-li-review__subtitle">
          {TRANSLATIONS.HOW_IT_WAS} &quot;<b>{order.listing.title}</b>
          &quot;.
        </p>
      ) : (
        ''
      )}
    </>
  );

  const modal = (
    <Modal
      isOpen={isOpen}
      onClickOverlay={() => null}
      shouldScroll={shouldScroll}
    >
      <div className="modal-ord-li-review">
        <ModalClose onClick={() => setIsOpen(false)} />
        <div className="w-100% mb-xs text-center">
          <Typography variant="h6" weight={TypographyWeights['semi-bold']}>
            {findTitle(order.reviewStatus)}
          </Typography>
        </div>
        {order.reviewStatus !== EDITABLE && experienceDescription}
        {reviewForm}
      </div>
      <div
        className={'modal-ord-li-review__review-form__footer'}
        style={{ display: isDeviceSmallerThanTablet ? 'none' : 'block' }}
      >
        {submitButton}
      </div>
    </Modal>
  );

  const drawer = (
    <Drawer
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title={
        <Typography variant="p2" weight={TypographyWeights['semi-bold']}>
          {TRANSLATIONS.INSERT_REVIEW}
        </Typography>
      }
      body={
        <div className="p-m d-flex flex-column">
          {experienceDescription}
          {reviewForm}
        </div>
      }
      footer={<div className="p-xs">{submitButton}</div>}
    />
  );

  return <>{isDeviceSmallerThanTablet ? drawer : modal}</>;
};

ModalOrderListReview.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  order: PropTypes.object,
  shouldScroll: PropTypes.bool.isRequired,
  onFinish: PropTypes.func.isRequired,
  tokenOrderCustomer: PropTypes.number,
  actionToken: PropTypes.string,
  reviewId: PropTypes.number,
};

ModalOrderListReview.defaultProps = {
  actionToken: null,
  tokenOrderCustomer: null,
};

export { ModalOrderListReview };
